import { cn } from "@gigpro/ui/tailwind/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs } from "radix-ui";
import React from "react";

const TabsVariantContext = React.createContext<TabsVariant | null>(null);
function useTabsVariant() {
  const variant = React.useContext(TabsVariantContext);
  if (variant === null) {
    throw new Error("Tabs components must be used within a TabsRoot");
  }
  return variant;
}

export type TabsVariant = VariantProps<typeof tabsListVariants>["variant"];
const tabsListVariants = cva("flex", {
  variants: {
    variant: {
      default: "",
      underline: "mb-2 bg-white px-4 shadow-down sm:px-8",
      filled: "relative isolate mb-2 overflow-visible rounded-sm bg-black",
      // Connected segmented "pipeline" bar: tabs flow into each other, dividers between, outer rounding.
      pipeline: "divide-x divide-light-gray overflow-hidden rounded-sm border border-light-gray",
    },
  },
  defaultVariants: { variant: "default" },
});

export interface TabsRootProps extends React.ComponentProps<typeof Tabs.Root> {
  variant?: Exclude<TabsVariant, undefined>;
}

export const TabsRoot = ({ variant = "default", children, ...props }: TabsRootProps) => (
  <TabsVariantContext.Provider value={variant}>
    <Tabs.Root {...props}>{children}</Tabs.Root>
  </TabsVariantContext.Provider>
);

export const TabsList = ({ className, ...props }: React.ComponentProps<typeof Tabs.List>) => {
  const variant = useTabsVariant();
  return <Tabs.List className={cn(tabsListVariants({ variant }), className)} {...props} />;
};

const tabsTriggerVariants = cva(
  "flex cursor-pointer items-center justify-center transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "flex-1 rounded-none border-b-2 border-transparent px-1 py-2 text-large font-regular text-gray data-[state=active]:border-b-brand data-[state=active]:font-semibold data-[state=active]:text-black sm:flex-none sm:justify-start sm:py-0",
        underline:
          "h-8 flex-1 border-b-2 border-transparent px-1 text-large font-semibold text-medium-gray data-[state=active]:border-b-brand data-[state=active]:text-black sm:flex-none",
        filled: cn(
          "relative min-h-8 flex-1 rounded-sm text-normal font-semibold text-white duration-300 data-[state=active]:bg-brand",
          // Arrow pointer below the active tab
          "after:absolute after:inset-[auto_0_-5px] after:m-[0_auto] after:hidden after:size-0 after:border-x-13 after:border-t-[6px] after:border-x-transparent after:border-t-brand data-[state=active]:after:block",
        ),
        // `group` lets segment content (count/label/chevron) react to this tab's state via group-data-[state=active]:.
        pipeline:
          "group flex-1 justify-start gap-2 rounded-none bg-white px-4 py-3 text-left data-[state=active]:bg-brand",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export const TabsTab = ({ className, ...props }: React.ComponentProps<typeof Tabs.Trigger>) => {
  const variant = useTabsVariant();
  return <Tabs.Trigger className={cn(tabsTriggerVariants({ variant }), className)} {...props} />;
};

export const TabsPanel = Tabs.Content;

export const TabsDivider = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={cn("mx-1 hidden h-[80%] w-[2px] bg-light-gray sm:block", className)} {...props} />
);
